import React, { useState } from 'react';
import { format } from 'date-fns';
import api from '../utils/api';
import ConfirmDialog from './ConfirmDialog';
import ModalShell from './ModalShell';

function withTime(dateTimeValue, timeValue) {
  const datePart = String(dateTimeValue || '').slice(0, 10);
  return `${datePart}T${timeValue}`;
}

function inferBlockType(reason) {
  const normalizedReason = String(reason || '').trim().toLowerCase();
  if (normalizedReason === 'urlop') return 'vacation';
  if (['przerwa', 'lunch'].includes(normalizedReason)) return 'break';
  if (['prywatne', 'dojazd'].includes(normalizedReason)) return 'private';
  return 'custom';
}

export default function AddBlockModal({ onClose, onSaved, defaultDate }) {
  const dateStr = defaultDate ? format(defaultDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
  const [form, setForm] = useState({
    reason: 'Prywatne',
    blocked_from: `${dateStr}T10:00`,
    blocked_until: `${dateStr}T11:00`,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({ blocked_from: '', blocked_until: '' });
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const REASONS = ['Prywatne', 'Przerwa', 'Lunch', 'Dojazd', 'Urlop', 'Inne'];

  async function handleSubmit(e) {
    e.preventDefault();
    const nextErrors = {
      blocked_from: !form.blocked_from ? 'Podaj początek blokady.' : '',
      blocked_until: !form.blocked_until ? 'Podaj koniec blokady.' : '',
    };

    setFieldErrors(nextErrors);

    if (nextErrors.blocked_from || nextErrors.blocked_until) {
      setError('Uzupełnij godziny blokady.');
      return;
    }

    if (!form.blocked_from || !form.blocked_until) {
      setError('Podaj czas blokady.');
      return;
    }
    if (new Date(form.blocked_from) >= new Date(form.blocked_until)) {
      setError('Koniec blokady musi być po jej początku.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = form.reason === 'Urlop'
        ? {
          ...form,
          block_type: inferBlockType(form.reason),
          blocked_from: withTime(form.blocked_from, '00:00'),
          blocked_until: withTime(form.blocked_until, '23:59'),
        }
        : {
          ...form,
          block_type: inferBlockType(form.reason),
        };

      await api.post('/availability/blocks', payload);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Błąd zapisu.');
    } finally {
      setSaving(false);
    }
  }

  function requestClose() {
    const hasUnsavedChanges = (
      form.reason !== 'Prywatne' ||
      form.blocked_from !== `${dateStr}T10:00` ||
      form.blocked_until !== `${dateStr}T11:00`
    );

    if (!hasUnsavedChanges || saving) {
      onClose();
      return;
    }

    setShowCloseConfirm(true);
  }

  return (
    <>
      <ModalShell title="Zablokuj czas" onClose={requestClose}>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label>Powód</label>
            <select
              value={form.reason}
              onChange={(e) => {
                setError('');
                setForm({ ...form, reason: e.target.value });
              }}
            >
              {REASONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Od</label>
            <input
              type="datetime-local"
              value={form.blocked_from}
              onChange={(e) => {
                setError('');
                setFieldErrors((prev) => ({ ...prev, blocked_from: '' }));
                setForm({ ...form, blocked_from: e.target.value });
              }}
              className={fieldErrors.blocked_from ? 'input-error' : ''}
              aria-invalid={fieldErrors.blocked_from ? 'true' : 'false'}
            />
            {fieldErrors.blocked_from && <div className="field-error">{fieldErrors.blocked_from}</div>}
            {form.reason === 'Urlop' && <div className="field-hint">Dla urlopu godzina startu zostanie ustawiona na początek dnia.</div>}
          </div>
          <div className="form-group">
            <label>Do</label>
            <input
              type="datetime-local"
              value={form.blocked_until}
              onChange={(e) => {
                setError('');
                setFieldErrors((prev) => ({ ...prev, blocked_until: '' }));
                setForm({ ...form, blocked_until: e.target.value });
              }}
              className={fieldErrors.blocked_until ? 'input-error' : ''}
              aria-invalid={fieldErrors.blocked_until ? 'true' : 'false'}
            />
            {fieldErrors.blocked_until && <div className="field-error">{fieldErrors.blocked_until}</div>}
            {form.reason === 'Urlop' && <div className="field-hint">Dla urlopu godzina końca zostanie ustawiona na koniec dnia.</div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-ghost" onClick={requestClose}>Anuluj</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Zapisuję...' : 'Zablokuj'}
            </button>
          </div>
        </form>
      </ModalShell>

      <ConfirmDialog
        open={showCloseConfirm}
        title="Zamknąć blokadę czasu?"
        description="Masz niezapisane zmiany blokady. Jeśli zamkniesz okno, wpisane godziny przepadną."
        confirmLabel="Zamknij bez zapisu"
        cancelLabel="Wracam"
        confirmTone="danger"
        onCancel={() => setShowCloseConfirm(false)}
        onConfirm={() => {
          setShowCloseConfirm(false);
          onClose();
        }}
      />
    </>
  );
}
